import React from "react";
import classNames from "classnames";
import { Lazy } from "@/components/Lazy";
import { optimizeCloudinaryImage } from "@/services/CloudinaryService";
import { IMAGE_VARIANTS, IMAGE_RETRY_CONFIG } from "../../constants";
import { logWarning } from "@/services/DatadogService";
import "./PreviewCloudinaryImage.scss";

export interface PreviewCloudinaryImageProps {
  src?: string;
  variant?: "logo-large" | "avatar" | "thumbnail" | "hero" | "gallery";
  alt?: string;
  isInitPreview?: boolean;
  className?: string;
  placeholderClass?: string;
  fallback?: string;
  [key: string]: any;
}

export function PreviewCloudinaryImage({
  src,
  variant = "hero",
  alt = "",
  isInitPreview = false,
  className,
  placeholderClass,
  fallback,
  ...rest
}: PreviewCloudinaryImageProps) {
  const [imageError, setImageError] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const { maxRetries, baseDelay } = IMAGE_RETRY_CONFIG;

  // In initial view, use unoptimized image
  const imageSrc = React.useMemo(() => {
    return isInitPreview
      ? src
      : optimizeCloudinaryImage({
          url: src,
          transformations: IMAGE_VARIANTS[variant] as any,
        });
  }, [src, isInitPreview, variant]);

  const handleImageError = React.useCallback(() => {
    if (retryCount < maxRetries) {
      logWarning("Preview image failed to load, attempting retry", {
        previewComponent: "PreviewCloudinaryImage",
        imageVariant: variant,
        retryCount: retryCount + 1,
        maxRetries,
        isInitPreview,
        src,
      });

      setRetryCount((prev) => prev + 1);
      // Reset error to trigger retry
      setTimeout(
        () => {
          setImageError(false);
        },
        baseDelay * (retryCount + 1)
      ); // Exponential backoff
    } else {
      logWarning("Preview image failed to load after all retry attempts", {
        previewComponent: "PreviewCloudinaryImage",
        imageVariant: variant,
        totalRetries: retryCount,
        maxRetries,
        isInitPreview,
        hasFallback: Boolean(fallback),
        src,
      });
      setImageError(true);
    }
  }, [retryCount, maxRetries, baseDelay, src, variant, isInitPreview, fallback]);

  // Early return after all hooks are defined
  if (!src || (imageError && retryCount >= maxRetries)) {
    return fallback ? (
      <img
        src={fallback}
        alt={alt || "Preview image fallback"}
        className={className}
        aria-label="Image failed to load, showing fallback"
        {...rest}
      />
    ) : null;
  }

  const imageClasses = classNames(
    "preview-cloudinary-image",
    `preview-cloudinary-image--${variant}`,
    className
  );

  // Determine automatic placeholder class if not provided
  const defaultPlaceholderClass = placeholderClass || `skeleton-placeholder__${variant}__image`;

  return (
    <Lazy placeholderClass={defaultPlaceholderClass}>
      <img
        src={imageSrc}
        alt={alt}
        className={imageClasses}
        onError={handleImageError}
        {...rest}
      />
    </Lazy>
  );
}
